import { useEffect, useState } from "react";
import { Box, CircularProgress, List, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import AddNewMeditationRecordButton from "./AddNewMeditationRecordButton";
import {
  MeditationGroupType,
  MeditationRecordType,
} from "api/meditationRecordApi";
import OpenStatisticsButton from "./OpenStatisticsButton";
import useGetMeditationRecordVersion from "hooks/useGetMeditationRecordVersion";
import TabButtonList from "../../common/TabButtonList";
import useIsInappWebview from "hooks/useIsInappWebview";
import MeditationRecordListItem from "./MeditationRecordListItem";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import useUnifiedNavigate from "../../../hooks/useUnifiedNavigate";

export default function MeditationRecordListForMobile({
  meditationRecordList,
  currentMeditationRecord,
  setCurrentMeditationRecord,
}: {
  meditationRecordList?: MeditationRecordType[];
  currentMeditationRecord?: MeditationRecordType;
  setCurrentMeditationRecord: React.Dispatch<
    React.SetStateAction<MeditationRecordType | undefined>
  >;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";
  const isInappWebview = useIsInappWebview();
  const navigate = useNavigate();

  const { isRoutineAvailable, isCopingAvailable } =
    useGetMeditationRecordVersion();

  const [existTabList] = useState<MeditationGroupType[]>(["routine", "coping"]);
  const [selectedTab, setSelectedTab] = useState<MeditationGroupType>(
    isRoutineAvailable ? "routine" : isCopingAvailable ? "coping" : "routine"
  );

  const unifiedNavigate = useUnifiedNavigate();
  const [isStatisticsOpen, setIsStatisticsOpen] = useState<boolean>(false);

  function openStatistics() {
    if (isInappWebview) {
      unifiedNavigate("/meditation-record-statistics", {
        type: "card",
        gestureEnabled: true,
        animation: "default",
      });
    }
    setIsStatisticsOpen(true);
    setCurrentMeditationRecord(undefined);
  }

  useEffect(() => {
    if (currentMeditationRecord?.type) {
      setSelectedTab(currentMeditationRecord.type);
    }
  }, [currentMeditationRecord]);

  const currentTabListData =
    meditationRecordList?.filter(
      (element) => !element.type || element.type === selectedTab
    ) ?? [];

  return (
    <Stack
      alignItems="center"
      sx={{
        flexShrink: 0,
        width: "100%",
        height: "100%",
        transition: "transform 0.4s, width 0.4s",
        scrollPaddingBlockStart: "20px",
        boxSizing: "border-box",
      }}
      className="scrollbar-hide"
    >
      <Stack
        direction={"column"}
        justifyContent={"center"}
        spacing={"16px"}
        sx={{
          width: "100%",
          // backgroundColor: "background.level1",
          flexShrink: 0,
          zIndex: 10,
          position: "sticky",
          py: "8px",
          top: 0,
          px: "20px",
        }}
      >
        <Stack
          spacing={1}
          direction={"column"}
          sx={{
            flexShrink: 0,
            width: "100%",
          }}
        >
          {existTabList && existTabList.length > 1 && (
            <TabButtonList
              selectedTab={selectedTab}
              data={[
                {
                  id: "routine",
                  label: "연습하기",
                  onClick: () => {
                    setSelectedTab("routine");
                    // 탭 전환 시 현재 선택을 해당 탭의 첫 항목으로 동기화
                    if (currentMeditationRecord?.type !== "routine") {
                      const targetList = (meditationRecordList || []).filter(
                        (element) => !element.type || element.type === "routine"
                      );
                      if (targetList.length > 0) {
                        setCurrentMeditationRecord(targetList[0]);
                      }
                    }
                  },
                },
                {
                  id: "coping",
                  label: "다스리기",
                  onClick: () => {
                    setSelectedTab("coping");
                    // 탭 전환 시 현재 선택을 해당 탭의 첫 항목으로 동기화
                    if (currentMeditationRecord?.type !== "coping") {
                      const targetList = (meditationRecordList || []).filter(
                        (element) => !element.type || element.type === "coping"
                      );
                      if (targetList.length > 0) {
                        setCurrentMeditationRecord(targetList[0]);
                      }
                    }
                  },
                },
              ]}
            />
          )}
          <AddNewMeditationRecordButton
            disabled={
              isCoach ||
              (selectedTab === "coping" && !isCopingAvailable) ||
              ((meditationRecordList || []).length === 1 &&
                !meditationRecordList?.every((element) => element.lastDoneAt))
            }
            groupType={selectedTab}
          />
        </Stack>
      </Stack>

      <List
        orientation={"vertical"}
        className="scrollbar-hide"
        sx={{
          py: 0,
          px: "20px",
          overflow: "scroll",
          width: "100%",
          "--ListItem-paddingY": 0,
          "--ListItem-paddingX": 0,
        }}
      >
        {isRoutineAvailable && selectedTab === "routine" && (
          <Box sx={{ mb: "12px" }}>
            <OpenStatisticsButton
              isOpen={isStatisticsOpen}
              open={openStatistics}
            />
          </Box>
        )}

        {meditationRecordList === undefined ? (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", py: 4 }}
          >
            <CircularProgress color={"neutral"} />
          </Stack>
        ) : currentTabListData && currentTabListData.length > 0 ? (
          currentTabListData.map((record, index) => {
            return (
              <MeditationRecordListItem
                key={record.meditationRecordKey}
                width={"100%"}
                direction={"column"}
                isFirstRecordOfMonth={
                  index === 0 ||
                  (index - 1 >= 0 &&
                    dayjs(currentTabListData[index - 1].date).get("month") !==
                      dayjs(currentTabListData[index].date).get("month"))
                }
                record={record}
                isSelected={
                  currentMeditationRecord?.meditationRecordKey ===
                  record.meditationRecordKey
                }
                setCurrentRecord={setCurrentMeditationRecord}
                shouldBeDone={!isCoach && record.shouldBeDone}
                durationMinutes={record.durationMinutes}
              />
            );
          })
        ) : (
          <Stack alignSelf={"center"} sx={{ width: "100%" }}>
            <Typography
              sx={{
                width: "100%",
                fontSize: "14px",
                opacity: 0.6,
                textAlign: "center",
              }}
            >
              {selectedTab === "routine"
                ? isRoutineAvailable
                  ? "진행한 훈련이 없어요."
                  : "진행한 훈련이 없어요."
                : isCopingAvailable
                  ? "진행한 훈련이 없어요."
                  : "아직 배우지 않았어요. 연습하기 훈련에 집중해주세요."}
            </Typography>
          </Stack>
        )}
      </List>
    </Stack>
  );
}
